import type { DateTime } from "luxon";
import type { IncentivePlan } from "./models";

// e.g. "0–24h" or "24h+"
export const formatWindowRange = (plan: IncentivePlan): string => (plan.endHour == null ? `${plan.startHour}h+` : `${plan.startHour}–${plan.endHour}h`);

// Decay curves have 2+ windows; flat/zero have 0 or 1.
export const hasDecayCurve = (windows: IncentivePlan[] | undefined): windows is IncentivePlan[] => !!windows && windows.length > 1;

export const sortedCurveWindows = (windows: IncentivePlan[] | undefined): IncentivePlan[] | null =>
	hasDecayCurve(windows) ? [...windows].sort((a, b) => a.startHour - b.startHour) : null;

// Next decay step after the active window, or null if none.
export const findNextWindow = (windows: IncentivePlan[] | undefined, postedAt: DateTime | undefined): IncentivePlan | null => {
	if (!windows || windows.length < 2 || !postedAt) return null;
	const sorted = [...windows].sort((a, b) => a.startHour - b.startHour);
	const elapsedHours = postedAt.diffNow().negate().as("hours");
	const activeIdx = sorted.findIndex((w) => w.startHour <= elapsedHours && (w.endHour == null || elapsedHours < w.endHour));
	if (activeIdx < 0 || activeIdx >= sorted.length - 1) return null;
	return sorted[activeIdx + 1];
};

// True when the bonus value is the same across all time — a single (0 → open-ended) window
// or no snapshot at all (treated as constant $0). Mirrors backend `windows_safe_to_dedup` shape.
export const isConstantBonusSnapshot = (windows: IncentivePlan[] | undefined): boolean => {
	const w = windows ?? [];
	if (w.length === 0) return true;
	return w.length === 1 && w[0].startHour === 0 && w[0].endHour == null;
};

// The single value of a constant snapshot. Callers must gate on `isConstantBonusSnapshot` first.
export const constantBonusValue = (windows: IncentivePlan[] | undefined): number => (windows && windows.length > 0 ? windows[0].value : 0);

// Two snapshots can be merged when both are constant AND share the same value (decay curves never merge).
export const canMergeBonus = (a: IncentivePlan[] | undefined, b: IncentivePlan[] | undefined): boolean =>
	isConstantBonusSnapshot(a) && isConstantBonusSnapshot(b) && constantBonusValue(a) === constantBonusValue(b);

// The windows a not-yet-posted shift WOULD have if posted right now — used to compare against
// existing snapshots for overlap detection.
export const intendedWindowsForNewShift = (bonus: number, templatePlans?: IncentivePlan[]): IncentivePlan[] => {
	if (templatePlans !== undefined) return templatePlans;
	if (bonus > 0) return [{ startHour: 0, endHour: null, value: bonus }];
	return [];
};

// Stable string key for grouping shifts that should merge visually (e.g. `RequestsContext.mergedRequests`).
// Constant snapshots → "c:<value>", decay → "d:<shiftId>" (always unique).
export const mergeGroupKey = (shiftId: string, windows: IncentivePlan[] | undefined): string =>
	isConstantBonusSnapshot(windows) ? `c:${constantBonusValue(windows)}` : `d:${shiftId}`;
